<script lang="ts" setup>
</script>

<template>
  <div v-once>
    <div class="document-page">
      <h2 class="document-title">Privacy Policy</h2>
      <div class="document-date">User Privacy Policy Version: 2022-v.1.1</div>
      <p>
        OpenAtom Foundation (hereinafter referred to as "we", "us", "our", and
        the "OpenAtom") is well aware of the importance of your privacy and
        respects your privacy. Please read and understand this Privacy Policy
        (this "Policy") before you provide any personal data to us. This Policy
        applies to any and all websites, contents and services that display or
        link to this privacy policy.
      </p>
      <p>
        This Policy aims to help you understand what data we collect, why we
        collect it, and how your personal data is used, accessed, modified,
        disclosed and deleted, but it may not address all possible data
        processing scenarios. We may inform you of content- or service-specific
        data collection through supplementary policies or notices provided
        before collection.
      </p>
      <p>This Policy is formulated to help you understand:</p>
      <ul>
        <li>1. How we collect and use your personal data</li>
        <li>2. How we use cookies and similar technologies</li>
        <li>3. How we share and disclose your personal data</li>
        <li>4. How to access or modify your personal data</li>
        <li>5. How we protect and retain your personal data</li>
        <li>6. How we process the personal data of minors</li>
        <li>7. How is your personal data transferred internationally</li>
        <li>8. How is this Policy updated?</li>
        <li>9. How to contact us</li>
      </ul>
      <h5>1. How we collect and use your personal data</h5>
      <p><b>1.1How we collect and use your personal data</b></p>
      <p>
        1.1.1Personal data refers to any data related to an identified or
        identifiable natural person recorded by electronic or other means,
        excluding anonymized data.
        <b
        >Before using our contents or services, you may need to provide
          personal data</b
        >. In some cases, you may be able to opt not to disclose your personal
        data to us. However, not providing us with certain data may mean that we
        cannot provide you with certain contents or services or respond to or
        solve an issue that you encounter. Your personal data will be submitted
        directly when you use our website (this "Website"), contents or
        services, or when you interact with us.
      </p>
      <p>
        1.1.2We may collect and use your personal data for the following
        purposes:
      </p>
      <p>
        1.1.2.1 In the following scenarios, we will collect your nickname,
        profile picture, phone number and/or personal email address ("General
        User Data"):
      </p>
      <ul>
        <li>(a) when you create an account on OpenAtom official website;</li>
        <li>(b) when you contact us for help;</li>
        <li>
          (c) to fulfill your service requests (including but not limited to
          offering compatibility assessment services, and managing and
          processing compatibility assessment results);
        </li>
      </ul>
      <p>
        The data we collect depends on how you interact with us. We collect your
        General User Data by recording how you interact with our website,
        contents or services (for example, with technologies such as cookies, or
        receiving usage data from software running on your device).
      </p>
      <p>
        1.1.2.2 In the following scenarios, we will collect your following data
        in addition to General User Data:
      </p>
      <ul>
        <li>
          (a) Your name, gender, business name, position, address, email
          address, telephone number, login information (account number and
          password), photo, <b>identity document information</b> if any OpenAtom
          activity involves;
        </li>
        <li>
          (b) Your <b>bank name, account name and bank account number</b> to
          fulfill your transaction request if payment is involved; your
          nickname, profile picture and user ID of the third-party payment
          platform you use, if any;
        </li>
        <li>
          (c) Information you provided to us and the content of the message you
          sent us, if any, for example, the inquiry information you entered, or
          the questions asked, or information provided to request customer
          service support.
        </li>
      </ul>
      <p>
        1.1.2.3 At present, we do not take the initiative to obtain your
        personal data from external third parties. If we need to obtain your
        personal data indirectly from a third party for business development in
        the future, we will clearly indicate to you the source, type and scope
        of use of your personal data in advance. If our processing of your
        personal data is beyond the scope of your consent to such a third party,
        we will obtain your explicit consent in advance. Meanwhile, we will
        strictly abide by the relevant laws and regulations, and ask the third
        party to guarantee the legality of the information provided by it.
      </p>
      <p>
        1.1.3 We may also collect and use non-personally identifiable
        information (Non-PII). Non-PII refers to data that cannot be used to
        identify a particular individual. For example, we will collect
        statistical data, such as the numbers of visits to a website. We collect
        this data to understand how users use our websites, content, and
        services so that we can improve our services and better satisfy your
        needs. We may collect, use, process, transfer, or disclose non-PII for
        other purposes at our own discretion. We will endeavor to isolate your
        personal data from non-PII and ensure that the two types of data are
        used separately. If personal data is combined with non-PII, it will
        still be treated as personal data during processing.
      </p>
      <p><b>1.2 How we use your personal data</b></p>
      <p>
        We will use your personal data for the following purposes in strict
        accordance with applicable laws, regulations, agreements with you, and
        this Policy:
      </p>
      <p>
        1.2.1 To achieve the purposes stated in Article 1.1 "How we collect and
        use your personal data", we undertake to use collected data to provide
        you with the following services, including but not limited to:
      </p>
      <ul>
        <li>
          (a)Contacting you with your consent;<br>
          For example: sending you information about content and services that
          may interest you; inviting you to participate in our activities
          (including promotional activities), market surveys, or satisfaction
          surveys; or sending you marketing information. If you do not want to
          receive this type of information, you can opt out at any time by
          sending an email to mis@openatom.org.
        </li>
        <li>
          (b) Sending you important notices, such as installation of and updates
          to operating system or applications.
        </li>
        <li>
          (c) Qualifying and managing suppliers and business partners, and
          communicating or working with suppliers and business partners.
        </li>
        <li>
          (d) Improving our content and services through internal audits, data
          analysis, and research.
        </li>
        <li>
          (e) Synchronizing, sharing, and storing the data you upload or
          download, and the data needed for the uploading and downloading.
        </li>
        <li>
          (f) Complying with and enforcing applicable legal requirements,
          industry standards and our policies.
        </li>
      </ul>
      <p>1.2.2. To conclude or perform a contract as necessary.</p>
      <p>
        1.2.3 To report to related departments in accordance with laws,
        regulations and regulatory requirements.
      </p>
      <p>
        <b
        >You understand and acknowledge that anonymous summary statistics are
          not personal user information as defined by applicable laws and
          regulations, and we will keep and use such information for a variety
          of purposes, including but not limited to identifying the purchase
          intention of relevant individuals to our products and services.</b
        >
      </p>
      <p>
        <b
        >If we use your personal data beyond the purposes stated at the time
          of collection and scope that is directly or reasonably related to such
          purposes, we will obtain your consent again before using your personal
          data in confirmation agreement, text confirmation action in specific
          scenarios, or other forms in accordance with the requirements of laws,
          regulations and national standards.</b
        >
      </p>
      <h5>2. How we use cookies and similar technologies</h5>
      <p><b>2.1 Cookie</b></p>
      <p>
        To ensure that our website functions properly, we may at times store a
        small piece of data known as a cookie on your computer or mobile device.
        A cookie is a text file stored by a web server on a computer or mobile
        device. The content of a cookie can be retrieved or read only by the
        server that creates the cookie. Cookies are unique to the browsers or
        mobile applications you use. The text in a cookie often consists of
        identifiers, site names, and some numbers and characters. Cookies enable
        websites to store data such as your preferences.
      </p>
      <p>
        Like many other websites or Internet service providers, we use cookies
        to improve user experience. Cookies enable websites to remember your
        every visit (session cookies) or multiple visits (persistent cookies).
        Cookies allow websites to remember your settings such as language, font
        size on your computer or mobile device, or other browser preferences.
        This means that a user does not need to reset preferences for every
        visit. On the contrary, if cookies are not used, websites will treat you
        as a new visitor every time you load a web page. For example, if you are
        redirected to another web page from a website you are already logged in
        to and then return to the original website, it will not recognize you
        and you must log in again. We will not use cookies for any purposes not
        stated in this Policy. You can manage or delete cookies based on your
        own preferences. You can clear all the cookies stored on your computer,
        and most web browsers provide the option of blocking cookies. However,
        by doing so, you have to change the user settings every time you visit
        our website.
      </p>
      <p><b>2.2. Website beacons and pixel tags</b></p>
      <p>
        In addition to cookies, we may also use other similar technologies on
        our websites such as website beacons and pixel tags. For example, when
        you receive an email from us, it may contain a click-through URL that
        links to our web page. If you click the link, we will track your visit
        to help us learn about your preferences for content and services and
        improve our customer service. A web beacon is a transparent graphic
        image embedded in a website or in an email. We use pixel tags in emails
        to find out whether an email has been opened. You can unsubscribe from
        our mailing list at any time if you do not want to be tracked in this
        manner.
      </p>
      <p>
        By using our website, you consent to the use of cookies, web beacons and
        pixel tags as described above.
      </p>
      <h5>3. How we share and disclose your personal data</h5>
      <p><b>3.1. Sharing</b></p>
      <p>
        We will not share your personal data with any other companies,
        organizations or individuals except in the following cases:
      </p>
      <p>
        3.1.1 We inform you of the recipient's name and contact information, the
        processing purpose and manner, type of personal data involved in advance
        and receive your consent or authorization.
      </p>
      <p>
        3.1.2 We may share your personal data externally in accordance with
        laws, regulations, to solve litigations or disputes, or as legally
        required by administrative or judicial authorities.
      </p>
      <p>
        3.1.3 Our business does not currently involve sharing your personal data
        with our affiliates. If we need to share your personal data with our
        affiliates for future business development, we will inform you of the
        recipient's name and contact information, the processing purpose and
        manner, type of personal data involved in advance. If data processing
        requires your consent, we will obtain your consent again before sharing
        your personal data with our affiliates.
      </p>
      <p>
        3.1.4 Sharing with authorized partners: We currently do not entrust
        authorized partners to provide you with services. If we need to share
        your personal data with authorized partners for future business
        development, we will only share some data with authorized partners to
        the extent necessary and reasonable. We will enter into a strict
        non-disclosure agreement or provision with companies, organizations, and
        individuals with which we share personal data, and require them to
        process personal data in accordance with our instructions, this Policy,
        and any other relevant non-disclosure and security measures. With
        respect to usage of sensitive personal data, we require third parties to
        adopt data masking and encryption technology for enhanced protection.
      </p>
      <p>
        <b
        >3.1.5 To ensure a positive user experience, you may receive content
          or web links from third parties other than OpenAtom and its partners.
          We do not have the right to control such third parties, but you can
          choose whether to access the links, content or services provided by
          third parties. We cannot control the privacy practices and data
          protection policies of third parties that are not subject to this
          Policy. When you submit personal data to a third party, please refer
          to the privacy protection policy of the third party.</b
        >
      </p>
      <p><b>3.2 Public Disclosure</b></p>
      <p>We will only disclose your personal data publicly:</p>
      <p>3.2.1 With your prior consent or authorization.</p>
      <p>
        3.2.2 If required by laws, regulations, mandatory administrative
        enforcement or judicial requirements.
      </p>
      <p>3.2.3 Under other circumstances stipulated by laws and regulations.</p>
      <h5>4. How to access or modify your personal data</h5>
      <p><b>4.1. Access to your personal data</b></p>
      <p>
        You can request to access your basic information by sending an email to
        mis@openatom.org. After receiving your request, we will respond to it
        promptly.
      </p>
      <p><b>4.2. Correct your personal data</b></p>
      <p>
        If you find that your personal data that is collected and stored by us
        is incorrect or needs to be updated, you can request an update by
        sending an email to mis@openatom.org. After receiving your request, we
        will respond to it promptly.
      </p>
      <p><b>4.3. Delete your personal data</b></p>
      <p>
        You can request that your data be deleted by sending an email to
        mis@openatom.org. After receiving your request, we will respond to it
        promptly.
      </p>
      <p>You can ask us to delete your personal data if:</p>
      <ul>
        <li>
          (a) We collected your personal data without your explicit consent, or
          you intend to withdraw your consent;
        </li>
        <li>
          (b) Our processing of your personal data violates laws and
          regulations;
        </li>
        <li>
          (c) Our processing or use of personal data violates our agreement with
          you;
        </li>
        <li>
          (d) You no longer use our products or services, or archive your
          account, and your personal data is beyond the retention period;
        </li>
        <li>
          (e) We no longer provide products or services to you and your personal
          data is beyond the retention period.
        </li>
      </ul>
      <p><b>4.4. Transfer your personal data</b></p>
      <p>
        If you want to transfer your personal data to a third-party data
        processor, you can submit a request by sending an email to
        mis@openatom.org. After receiving your request, we will respond to it
        promptly and assist you in your transfer.
      </p>
      <p><b>4.5 Withdraw your consent</b></p>
      <p>
        You can request to withdraw your consent by sending an email to
        mis@openatom.org. When you withdraw consent, we will not be able to
        continue to provide you with the services for which you have withdrawn
        your consent. However, withdrawal does not affect our processing of your
        personal data based on your consent before the withdrawal is made.
      </p>
      <p><b>4.6. Obtain a copy of your data</b></p>
      <p>
        You have the right to obtain a copy of your personal data. To do so, you
        can send an email to mis@openatom.org. After receiving your request, we
        will respond to it and promptly take related action.
      </p>
      <p>
        For us to respond to your request to access, correct or delete your
        personal data or withdraw your consent as stated above, you may need to
        prove your identity by a written request or in other methods for
        security purposes. We will respond to your request within a reasonable
        time.
      </p>
      <p><b>4.7 Circumstances where we cannot respond to your request</b></p>
      <p>We are unable to respond to your request if:</p>
      <ul>
        <li>
          (a) It is related to data controller's performance of legal
          obligations;
        </li>
        <li>
          (b) It is directly related to national security and national defense
          security;
        </li>
        <li>
          (c) It is directly related to public security, public health, and
          major public interests;
        </li>
        <li>
          (d) It is directly related to a criminal investigation, prosecution,
          trial or enforcement of sentence;
        </li>
        <li>
          (e) The data controller has sufficient evidence indicating that you
          have subjective malicious intentions or have abused your rights;
        </li>
        <li>
          (f) It is necessary to safeguard the life, property, and other
          significant legitimate rights and interests of you or other
          individuals, but your consent is difficult to obtain;
        </li>
        <li>
          (g) Responding to it will cause serious damage to the legitimate
          rights and interests of you or other individuals or organizations;
        </li>
        <li>(h) It involves trade secrets.</li>
      </ul>
      <p>
        In addition to the above rights, you have the right to decide, restrict,
        refuse and make automatic decisions about your personal data. To
        exercise those rights, you can also submit a request by sending an email
        to mis@openatom.org. After receiving your request, we will respond to it
        and promptly take related actions to ensure the effective exercise of
        your rights.
      </p>
      <h5>5. How we protect and retain your personal data</h5>
      <p>
        The security of your personal data is important to us. We use
        appropriate physical, management, and technical measures to protect your
        personal data from unauthorized access, disclosure, use, modification,
        damage, or loss. For example, we use cryptographic technologies for data
        confidentiality, protection mechanisms to prevent attacks, and access
        control mechanisms to permit only authorized access to your personal
        data. We also provide training on security and privacy protection for
        employees to raise their awareness of personal data protection. We are
        committed to protecting your personal data; however, please note that no
        security measure is perfect.
      </p>
      <p>
        We will retain your personal data for no longer than is necessary for
        the purposes stated in this Policy, unless otherwise extending the
        retention period is required or permitted by law. The data storage
        period may vary with scenario, content, and service. The standards
        OpenAtom uses to determine the retention period are as follows: the time
        required to retain personal data to fulfill business purposes, including
        providing content and services; maintaining corresponding transaction
        and business records; controlling and improving the performance and
        quality of products and services; ensuring the security of systems,
        contents, and services; handling possible user queries or complaints and
        locating problems; whether the user agrees to a longer retention period;
        and whether the laws, contracts, and other equivalencies have special
        requirements for data retention; etc. We will maintain your registration
        information as long as your account is necessary for service provision.
        You can choose to archive your account. After you deregister your
        account, we will stop providing you with content and services through
        your account and delete your relevant personal data, provided that
        deletion is not otherwise stipulated by special legal requirements.
      </p>
      <h5>6. How we process the personal data of minors</h5>
      <p>
        Our websites, content and services are primarily intended for adults. A
        child (under the age of 14) must not create an OpenAtom account without
        the consent of the child's parent or guardian. If a child's personal
        data is collected with prior parental consent, we will only use or
        disclose the data as permitted by law, with the explicit consent of the
        child's parents or guardians, or when necessary for the protection of
        the child. If we accidentally collect a child's personal data without
        verified prior consent from the child's parents, we will attempt to
        delete the data as soon as possible.
      </p>
      <h5>7. How is your personal data transferred internationally</h5>
      <p>
        As a global foundation, your personal data collected by us may be
        processed or accessed in the country/region where you use our content
        and services or in other countries/regions where we or our affiliates,
        service providers or business partners have a presence. These
        jurisdictions may have different data protection laws. In this case, we
        will take measures to ensure that data is processed as required by this
        Policy and applicable laws.
      </p>
      <h5>8. How is this Policy updated?</h5>
      <p>
        OpenAtom reserves the right to update or change this Policy at any time.
        We will release the latest Privacy Policy on this page for any changes.
        If major changes are made to the Privacy Policy, we may notify you
        through different channels, for example, posting a notice on our website
        or sending you a direct notification.
      </p>
      <h5>9. How to contact us</h5>
      <p>
        If you have any privacy questions or complaints, please send an email to
        legal@openatom.org. Generally, we will strive to reply within 15 working
        days. If you are not satisfied with our reply, especially when you think
        our processing of your personal data has harmed your legitimate rights
        and interests, you can also file a lawsuit at the competent court where
        OpenAtom is located.
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.document-page {
  width: 1200px;
  margin: 80px auto;
}

.document-page .document-title {
  color: #222;
  font-weight: 700;
  font-size: 42px;
  text-align: center;
}

.document-page .document-date {
  margin-top: 30px;
  color: #222;
  font-weight: 700;
  font-size: 18px;
  text-align: center;
}

.document-page > p {
  margin-top: 50px;
  color: #333;
  font-size: 18px;
  line-height: 28px;
}

.document-page > ul > li {
  color: #333;
  font-size: 18px;
  line-height: 28px;
}

.document-page > h5 {
  margin-top: 50px;
  color: #222;
  font-weight: 700;
  font-size: 24px;
  line-height: 150%;
}

.document-page b {
  text-decoration: underline;
}

@media (max-width: 1279.9px) {
  .document-page {
    width: auto;
    max-width: 1200px;
    padding: 0 24px;
  }
}

@media (max-width: 767.9px) {
  .document-page {
    margin: 40px auto;
    padding: 0 16px;
  }

  .document-page .document-title {
    font-size: 28px;
  }

  .document-page > p,
  .document-page > ul > li {
    font-size: 15px;
    line-height: 26px;
  }

  .document-page > h5 {
    font-size: 20px;
  }
}
</style>
